// Tela da Gestão Comercial na Visão Geral (#6496): Talita Bastos, que cuida de todo
// o comercial, parceiros inclusive.
//
// Pergunta da tela: vamos bater a meta de energia contratada, e onde o funil está
// travando? Os números reaproveitam as contas que já existem:
//   - energia e conversão: o funil do CRM, a mesma conta da aba Comercial do Painel
//     Executivo e dos KPIs da aba CEO;
//   - jornada do mês, comissão garantida e resultado por pessoa: a faixa "Resultado
//     do mês" do Kanban (#6426), com a meta de cada um da Folha de Pagamento;
//   - leads sem primeiro contato: a marca do SLA do Analytics Comercial;
//   - de onde vêm os contratos e a rede de parceiros (#6507): as mesmas negociações do
//     Painel, pela origem do lead e pelo parceiro dono.
// A meta do time é a do mês na aba Metas do Painel Executivo (`metas_mensais`).
import React, { useCallback, useMemo } from 'react';
import { BarChart3, KanbanSquare, ListChecks } from 'lucide-react';
import { useComercialDashboardData } from '../../Dashboard/PainelExecutivo/sections/comercial/useComercialDashboardData';
import { numerosComerciaisDoMes } from '../../Dashboard/PainelExecutivo/sections/ceo/useContextoKpis';
import { useKpisDepartamento } from '../../Dashboard/PainelExecutivo/sections/ceo/useCeoDados';
import { useMetasMensais } from '../../Dashboard/PainelExecutivo/sections/metas/useMetasMensais';
import { useResultadoMes } from '../../CRM/Kanban/useResultadoMes';
import { useMotivosReprovacao } from '../../../src/hooks/useMotivosReprovacao';
import { rotuloMotivoReprovacaoAnalise } from '../../../src/lib/motivosReprovacao';
import { kpiAutomatico, type ContextoKpis } from '../../../src/lib/dashboard/kpisAutomaticos';
import { formatarValorKpi } from '../../../src/lib/dashboard/kpisDepartamento';
import { rotuloDoMes } from '../../../src/lib/dashboard/metasMensais';
import { getDepartamentoComercial } from '../../../src/lib/dashboard/departamentoComercial';
import { rotuloFuncaoComercial } from '../../../src/lib/departamentos';
import { avaliarKpi, type KpiAvaliado } from '../../../src/lib/visaoGeral/diretoria';
import {
  FAIXAS_CONTA, projecaoDoMes, resultadoPorCanal, type Agrupamento, type Canal, type VendedorDoMes,
} from '../../../src/lib/visaoGeral/comercial';
import { lerMes } from '../../../src/lib/visaoGeral/periodo';
import { BlocosDoDia } from '../BlocosDoDia';
import { CabecalhoVisaoGeral, CartaoHoje, CartaoIndicador, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural } from '../componentes';
import { composicaoDaEnergia, useEnergiaPorCanal } from '../useEnergiaPorCanal';
import { PainelOrigemDosContratos, PainelRedeDeParceiros } from '../blocosComercialFase2';
import { useOrigemDosContratos, useRedeDeParceiros } from '../useComercialFase2';
import {
  DIAS_SIMULACAO_PARADA, DIAS_TERMO_SEM_ASSINATURA, useHojeComercial, useMetasVendedores, useMotivosDoMes,
} from '../useGestaoComercial';
import type { PropsTelaVisaoGeral } from '../tipos';

const kwh = (v: number | null | undefined) => formatarValorKpi(v ?? null, 'kwh');
const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%`;
const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/** "há 3 dias", "há 5 h" — para o lead mais antigo sem contato. */
function haQuanto(iso: string | null): string {
  if (!iso) return '';
  const h = Math.floor((Date.now() - Date.parse(iso)) / 3_600_000);
  return h >= 48 ? `há ${Math.floor(h / 24)} dias` : `há ${Math.max(h, 0)} h`;
}

function comparacaoDe(k: KpiAvaliado | null, parcial: boolean) {
  if (!k || k.melhora == null || !Number.isFinite(k.melhora) || Math.abs(k.melhora) < 0.005 || !k.rotuloAnterior) return undefined;
  const melhorou = k.melhora > 0;
  return {
    texto: `${pct(Math.abs(k.melhora))} ${melhorou ? 'melhor' : 'pior'} que ${k.rotuloAnterior}${parcial ? ' no mesmo ponto' : ''}`,
    bom: melhorou,
    sobe: k.sentido === 'maior' ? melhorou : !melhorou,
  };
}

/** Meta definida (no mês ou validada). A sugerida não aparece: sai do melhor mês recente e não é meta de ninguém. */
function metaDefinida(k: KpiAvaliado | null) {
  if (!k || k.carregando || k.meta <= 0 || (k.origemMeta !== 'mes' && k.origemMeta !== 'validada')) return undefined;
  const rotulo = k.origemMeta === 'mes' ? `meta de ${rotuloDoMes(k.mesMeta)}` : 'meta';
  const parcial = k.metaAteAgora < k.meta - 1e-9;
  return {
    texto: `${rotulo} ${formatarValorKpi(k.meta, k.unidade)}${parcial ? ` · ${formatarValorKpi(k.metaAteAgora, k.unidade)} até hoje` : ''}`,
    atingido: k.atingimento,
  };
}

const TelaComercial: React.FC<PropsTelaVisaoGeral> = ({ pessoa, definicao, periodo, aoMudarMes }) => {
  const { agora, inicioIso, fimIso, mes, ano } = useMemo(() => {
    const { ano, mes0 } = lerMes(periodo.mes)!;
    return {
      agora: periodo.corrente ? new Date() : new Date(ano, mes0 + 1, 0, 23, 59, 59),
      inicioIso: new Date(ano, mes0, 1).toISOString(),
      fimIso: new Date(ano, mes0 + 1, 1).toISOString(),
      mes: mes0 + 1,
      ano,
    };
  }, [periodo.mes, periodo.corrente]);
  const parcial = periodo.corrente;

  // ── dados ──
  const com = useComercialDashboardData();
  const { kpis: kpisSalvos } = useKpisDepartamento();
  const { metas: metasMensais } = useMetasMensais();
  const hoje = useHojeComercial();
  const motivos = useMotivosDoMes(inicioIso, fimIso);
  const { motivos: listaMotivosReprovacao } = useMotivosReprovacao();

  const ctx = useMemo<ContextoKpis>(() => ({
    hoje: agora, faturas: null, parteVigor: null, metaFaturamentoUsinas: 0,
    comercial: com.dados.length ? numerosComerciaisDoMes(com.dados, com.profilesById, agora) : null,
    marketing: null, geracao: null, paradas: null, energia: null, chamados: null,
  }), [agora, com.dados, com.profilesById]);
  const kpi = (id: string) => avaliarKpi(
    kpiAutomatico(id), ctx, kpisSalvos.find(k => k.calculo_automatico === id && k.status === 'validado'), metasMensais,
  );
  const energia = kpi('com_kwh');
  const conversao = kpi('com_conversao');
  const energiaCanais = useEnergiaPorCanal(com.dados, com.profilesById, periodo);
  const origens = useOrigemDosContratos(com.dados, periodo);
  const rede = useRedeDeParceiros(com.dados, com.profilesById, periodo);
  const deOndeVeio = energiaCanais ? composicaoDaEnergia(energiaCanais, periodo.anterior.rotulo) : null;
  const metaEnergia = metaDefinida(energia);
  const projecao = parcial && energia && !energia.carregando && energia.atual != null ? projecaoDoMes(energia.atual, periodo.fracao) : null;

  // Jornada do mês e comissão garantida: a mesma apuração da faixa do Kanban, para o time inteiro.
  const perfis = useMemo(() => [...com.profilesById.values()], [com.profilesById]);
  const escopoTodos = useCallback(async () => null, []);
  const todosVisiveis = useCallback(() => true, []);
  const resultado = useResultadoMes({
    selecionados: [], escopoResponsaveis: escopoTodos, perfis, responsavelVisivel: todosVisiveis, ativo: perfis.length > 0, mes: periodo.mes,
  });
  const idsPessoas = useMemo(() => resultado.pessoas.map(p => p.responsavelId), [resultado.pessoas]);
  const metasVendedores = useMetasVendedores(idsPessoas, mes, ano);
  const canais = useMemo(() => {
    if (resultado.atualizadoEm == null) return null;
    const vendedores: VendedorDoMes[] = resultado.pessoas.map(p => ({
      id: p.responsavelId,
      nome: p.nome,
      canal: getDepartamentoComercial(com.profilesById.get(p.responsavelId)) as Canal,
      aprovadas: p.aprovadas.length,
      kwhAprovado: p.aprovadas.reduce((t, a) => t + (Number(a.consumo_medio_kwh) || 0), 0),
      assinados: p.assinados.qtd,
      emAssinatura: p.emAssinatura.qtd,
      metaKwh: metasVendedores?.get(p.responsavelId) ?? null,
    }));
    return resultadoPorCanal(vendedores);
  }, [resultado.pessoas, resultado.atualizadoEm, metasVendedores, com.profilesById]);
  const licitacaoDoMes = energiaCanais?.atual.find(p => p.canal === 'licitacao');

  const carregandoResultado = resultado.atualizadoEm == null;
  const t = resultado.total;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} aoMudarMes={aoMudarMes} />

      <BlocosDoDia pessoa={pessoa} perfil="comercial" />

      <SecaoVisaoGeral titulo="Hoje" colunas={5} subtitulo={periodo.corrente ? undefined : 'sempre a situação de agora, qualquer que seja o mês'}>
        <CartaoHoje
          valor={hoje.leads ? hoje.leads.total : null}
          rotulo="leads esperando o 1º contato"
          tom={hoje.leads && hoje.leads.mais24h > 0 ? 'critico' : 'atencao'}
          para="/crm/analytics"
          dica={hoje.leads && hoje.leads.total > 0
            ? `Closers e BDRs, atribuídos e sem nenhuma atividade ainda. ${hoje.leads.mais24h} há mais de 24 h; o mais antigo ${haQuanto(hoje.leads.maisAntigo)}. Mesma marca do SLA de atendimento do Analytics Comercial.`
            : 'Closers e BDRs: leads atribuídos sem nenhuma atividade ainda.'}
        />
        <CartaoHoje
          valor={hoje.simulacoesParadas}
          rotulo={`simulações paradas há ${DIAS_SIMULACAO_PARADA}+ dias`}
          tom="atencao"
          para="/crm"
          dica={`Simulação feita e nenhuma atividade há ${DIAS_SIMULACAO_PARADA} dias ou mais. Leads ainda em prospecção ficam de fora: são milhares de listas frias e geográficas.`}
        />
        <CartaoHoje
          valor={hoje.termosParados}
          rotulo={`termos sem assinatura há ${DIAS_TERMO_SEM_ASSINATURA}+ dias`}
          tom="atencao"
          para="/crm"
          dica={`Termo enviado há ${DIAS_TERMO_SEM_ASSINATURA} dias ou mais (contando o último envio) e o cliente ainda não assinou.`}
        />
        <CartaoHoje
          valor={hoje.comPendencia ? hoje.comPendencia.qtd : null}
          rotulo="assinados com pendência na validação"
          detalhe={hoje.comPendencia ? kwh(hoje.comPendencia.kwh) : undefined}
          tom="atencao"
          para="/crm"
          dica={`Termo assinado que a validação de dados já analisou e devolveu pedindo documento, garantia ou confirmação do cliente${hoje.comPendencia ? ` (${hoje.comPendencia.documento} de documento, com o selo Pendência documental no Kanban)` : ''}. Fica fora de "em validação de dados".`}
        />
        <CartaoHoje
          valor={hoje.emValidacao ? kwh(hoje.emValidacao.kwh) : null}
          rotulo="em validação de dados"
          detalhe={hoje.emValidacao ? plural(hoje.emValidacao.qtd, 'negociação', 'negociações') : undefined}
          vazio={hoje.emValidacao?.qtd === 0}
          tom="atencao"
          para="/gestao"
          dica="Termos assinados na fila da analista, esperando a primeira análise ou a reanálise. Quem já foi analisado e está com pendência fica no card ao lado. Mesma conta da etapa Em Validação de Dados do Analytics Comercial."
        />
      </SecaoVisaoGeral>
      {hoje.erro && <p className="-mt-6 text-xs text-red-600 dark:text-red-400">{hoje.erro}</p>}

      <SecaoVisaoGeral titulo="O mês" subtitulo={periodo.rotulo}>
        <CartaoIndicador
          titulo="Energia aprovada"
          valor={energia && !energia.carregando ? kwh(energia.atual) : null}
          meta={metaEnergia}
          detalhe={energia && !energia.carregando
            ? [
                projecao != null ? `No ritmo de até hoje, fecha ${rotuloDoMes(periodo.mes)} com ${kwh(projecao)}${metaEnergia && energia.meta > 0 ? ` (${pct(projecao / energia.meta)} da meta)` : ''}.` : null,
                !metaEnergia ? `Sem meta para ${rotuloDoMes(energia.mesMeta)}: a diretoria define na aba Metas do Painel Executivo.` : null,
              ].filter(Boolean).join(' ') || undefined
            : undefined}
          composicao={deOndeVeio?.composicao}
          comparacao={comparacaoDe(energia, parcial)}
          nota={deOndeVeio?.nota}
          origem="kWh das negociações aprovadas no mês, todos os canais e licitações incluídas (funil do CRM, mesma conta do Painel Executivo)."
        />
        <CartaoIndicador
          titulo="Termos assinados no mês"
          valor={carregandoResultado ? null : `${t.assinados.qtd}`}
          detalhe={carregandoResultado ? undefined
            : `${kwh(t.assinados.kwh)}. Destes: ${t.validacao.qtd} em validação, ${t.pendencia.qtd} com pendência, ${t.reprovados.qtd} ${t.reprovados.qtd === 1 ? 'reprovado' : 'reprovados'} e ${t.aprovados.qtd} ${t.aprovados.qtd === 1 ? 'aprovado' : 'aprovados'}${t.saidos.qtd ? `; ${plural(t.saidos.qtd, 'saiu', 'saíram')} depois de assinar` : ''}. Em assinatura: ${plural(t.emAssinatura.qtd, 'termo', 'termos')}.`}
          para="/crm"
          origem="Jornada do mês, a mesma da faixa Resultado do mês do Kanban (#6426): as etapas somam os assinados."
        />
        <CartaoIndicador
          titulo="Conversão do funil"
          valor={conversao && !conversao.carregando ? formatarValorKpi(conversao.atual, 'percentual') : null}
          meta={metaDefinida(conversao)}
          detalhe="Contratos aprovados ÷ prospecções do mês."
          comparacao={comparacaoDe(conversao, false)}
          para="/crm/analytics"
        />
        <CartaoIndicador
          titulo="Comissão garantida do time"
          valor={carregandoResultado ? null : t.comissao == null ? '—' : reais(t.comissao)}
          detalhe="Das aprovadas no mês, pelas regras da Folha de Pagamento, como se o mês fechasse hoje. Sem salário e sem bônus que só se decide no fechamento."
          origem="Mesma conta da faixa Resultado do mês do Kanban."
        />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>

        <PainelDetalhe titulo="Resultado por canal e vendedor" acao={{ rotulo: 'Analytics Comercial', para: '/crm/analytics' }}>
          {canais === null ? (
            <div className="h-40 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : canais.length === 0 && !licitacaoDoMes?.kwh ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Ninguém com termo enviado, assinado ou aprovado em {rotuloDoMes(periodo.mes)}.</p>
          ) : (
            <div className="-mx-5 overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-400">
                    <th className="px-5 py-2 text-left font-semibold">Canal / pessoa</th>
                    <th className="px-3 py-2 text-right font-semibold" title="Termos enviados no mês que esperam assinatura">Em assinatura</th>
                    <th className="px-3 py-2 text-right font-semibold">Assinados</th>
                    <th className="px-3 py-2 text-right font-semibold" title="Aprovadas no mês: base da comissão">Aprovados</th>
                    <th className="px-3 py-2 text-right font-semibold">kWh aprovado</th>
                    <th className="px-3 py-2 text-right font-semibold" title="Meta de kWh do mês na Folha de Pagamento">Meta</th>
                    <th className="px-5 py-2 text-right font-semibold">% da meta</th>
                  </tr>
                </thead>
                <tbody>
                  {canais.map(c => (
                    <React.Fragment key={c.canal}>
                      <tr className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30 font-semibold text-gray-800 dark:text-gray-100">
                        <td className="px-5 py-2 whitespace-nowrap">{c.rotulo}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.pessoas.reduce((s, p) => s + p.emAssinatura, 0)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.pessoas.reduce((s, p) => s + p.assinados, 0)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.aprovadas}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{kwh(c.kwhAprovado)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.metaKwh > 0 ? kwh(c.metaKwh) : '—'}</td>
                        <td className="px-5 py-2 text-right tabular-nums">{c.atingimento == null ? '—' : pct(c.atingimento)}</td>
                      </tr>
                      {c.pessoas.map(p => (
                        <tr key={p.id} className="border-t border-gray-50 dark:border-gray-700/60 text-gray-600 dark:text-gray-300">
                          <td className="py-1.5 pl-9 pr-3 whitespace-nowrap">
                            {p.nome}
                            {rotuloFuncaoComercial(com.profilesById.get(p.id)) && (
                              <span className="ml-1.5 text-[11px] text-gray-400 dark:text-gray-500">· {rotuloFuncaoComercial(com.profilesById.get(p.id))}</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{p.emAssinatura}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{p.assinados}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{p.aprovadas}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{kwh(p.kwhAprovado)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{p.metaKwh ? kwh(p.metaKwh) : '—'}</td>
                          <td className={`px-5 py-1.5 text-right tabular-nums font-semibold ${p.atingimento == null ? 'text-gray-400' : p.atingimento >= 1 ? 'text-emerald-600 dark:text-emerald-400' : p.atingimento >= 0.7 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                            {p.atingimento == null ? '—' : pct(p.atingimento)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {licitacaoDoMes && licitacaoDoMes.kwh > 0 && (
                    <tr className="border-t border-gray-100 dark:border-gray-700 bg-amber-50/60 dark:bg-amber-900/10 font-semibold text-amber-800 dark:text-amber-200">
                      <td className="px-5 py-2">Licitação <span className="font-normal">(sem vendedor)</span></td>
                      <td className="px-3 py-2 text-right">—</td>
                      <td className="px-3 py-2 text-right">—</td>
                      <td className="px-3 py-2 text-right tabular-nums">{licitacaoDoMes.contratos}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{kwh(licitacaoDoMes.kwh)}</td>
                      <td className="px-3 py-2 text-right">—</td>
                      <td className="px-5 py-2 text-right">—</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            Aprovados = aprovação no mês, a base da comissão. Meta de cada pessoa: a da Folha de Pagamento (a última definida vale até mudar).
            Canal pelo cadastro de quem responde pela negociação, como no Painel Executivo.
          </p>
        </PainelDetalhe>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PainelOrigemDosContratos linhas={origens} rotuloMes={rotuloDoMes(periodo.mes)} />
          <PainelRedeDeParceiros rede={rede} rotuloMes={rotuloDoMes(periodo.mes)} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PainelDetalhe titulo="Por que reprovaram">
            <ListaMotivos
              grupo={motivos.reprovacoes}
              rotulo={codigo => rotuloMotivoReprovacaoAnalise(codigo, listaMotivosReprovacao) ?? codigo}
              vazio={`Nenhuma reprovação na validação de dados em ${rotuloDoMes(periodo.mes)}.`}
            />
            <p className="text-xs text-gray-400 mt-3">
              Motivo principal escolhido pela analista na reprovação. Uma negociação reanalisada conta uma vez, pela última decisão.
            </p>
          </PainelDetalhe>
          <PainelDetalhe titulo="Por que perdemos">
            <ListaMotivos grupo={motivos.perdas} vazio={`Nenhuma negociação perdida em ${rotuloDoMes(periodo.mes)}.`} estimado />
            {motivos.perdas && motivos.perdas.total.qtd > 0 && (
              <p className="text-xs text-gray-400 mt-3">
                kWh real quando a negociação tem consumo; nas demais, estimado pela faixa da conta de luz do formulário
                {FAIXAS_CONTA.map(f => {
                  const e = motivos.estimativas[f.id];
                  if (!e) return null;
                  return <span key={f.id}> · {f.rotulo} ≈ {kwh(e.kwh)}{e.base === 'mediana' ? ` (mediana de ${e.amostras} negociações)` : ` (R$ ${f.meio}, o meio da faixa, ÷ tarifa)`}</span>;
                })}.
                {motivos.perdas.total.semConsumo > 0 && ` ${plural(motivos.perdas.total.semConsumo, 'perda ficou', 'perdas ficaram')} sem consumo e sem faixa, fora do kWh.`}
              </p>
            )}
          </PainelDetalhe>
        </div>
        {motivos.erro && <p className="text-xs text-red-600 dark:text-red-400">{motivos.erro}</p>}
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'CRM', para: '/crm', icone: KanbanSquare },
        { rotulo: 'Analytics Comercial', para: '/crm/analytics', icone: BarChart3 },
        { rotulo: 'Atividades e visitas', para: '/tarefas', icone: ListChecks },
      ]} />
    </div>
  );
};

// ─────────────── lista de motivos ───────────────

export const ListaMotivos: React.FC<{ grupo: Agrupamento | null; vazio: string; rotulo?: (m: string) => string; estimado?: boolean }> = ({ grupo, vazio, rotulo, estimado }) => {
  if (grupo === null) return <div className="h-32 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />;
  if (grupo.total.qtd === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">{vazio}</p>;
  const maior = grupo.motivos[0]?.qtd || 1;
  return (
    <>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
        <strong className="text-gray-900 dark:text-white">{grupo.total.qtd.toLocaleString('pt-BR')}</strong> no mês ·{' '}
        <strong className="text-gray-900 dark:text-white">{kwh(grupo.total.kwhReal + grupo.total.kwhEstimado)}</strong>
        {estimado && grupo.total.kwhEstimado > 0 && <span className="text-gray-400"> ({kwh(grupo.total.kwhEstimado)} estimados)</span>}
      </p>
      <ul className="space-y-2.5">
        {grupo.motivos.slice(0, 7).map(m => (
          <li key={m.motivo}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 truncate text-gray-700 dark:text-gray-200" title={rotulo ? rotulo(m.motivo) : m.motivo}>{rotulo ? rotulo(m.motivo) : m.motivo}</span>
              <span className="whitespace-nowrap tabular-nums text-gray-500 dark:text-gray-400">
                <strong className="text-gray-900 dark:text-white">{m.qtd}</strong> · {kwh(m.kwhReal + m.kwhEstimado)}
                {estimado && m.kwhEstimado > 0 && m.kwhReal === 0 && <span className="text-gray-400"> est.</span>}
              </span>
            </div>
            <span className="mt-1 block h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <span className="block h-full rounded-full bg-primary/70" style={{ width: `${(m.qtd / maior) * 100}%` }} />
            </span>
          </li>
        ))}
      </ul>
      {grupo.motivos.length > 7 && (
        <p className="text-xs text-gray-400 mt-2">Mais {plural(grupo.motivos.length - 7, 'motivo', 'motivos')} com menos casos.</p>
      )}
    </>
  );
};

export default TelaComercial;
